<!--
  Storico Ordini — Dettagli (modale principale).
  Redesigned 2026-06-15 (v2.9.0, Phase 1.5 Chunk H.2).

  Drops kendo-dialog + ~15 kendo-label + 3× kendo-panelbar + 3×
  kendo-panelbar-item + ~10 kendoTextBox + kendo-grid errori + 5×
  kendo-grid-column + kendo-accordion + kendo-dropdownlist +
  kendoGridCellTemplate + ~50 inline style blocks (58 kendo refs → 0).

  Pattern Diakonix modale XL (mirror borderò-dettagli /
  status-spedizioni-dettaggli):
    - Backdrop z-index 2000 (sopra sidebar 1001, sotto sub-modali
      errori/modify-error a 2100)
    - Sticky header con chip viola "Parziale" se or_partial='S'
    - Body 2-col info-grid (ordine + spedizione) + 4 accordion
    - Footer "Chiudi"

  Bug fix legacy:
    - Drop `environment === urlServer` feature gate (la sezione errori
      non si attivava mai in prod). Ora sempre visibile.
    - groupBoxes ora chiamato nel subscribe.next (era in ngOnInit
      prima di avere this.box → primo call su array vuoto).

  Sub-modali preserved:
    - app-product-detail-modal (storicoOn) — dettagli prodotto
    - app-errori (H.3) — aggiungi errore
    - app-modify-error (H.4) — modifica errore
-->

@if (opened) {
  <div class="modal-backdrop" (click)="close()">
    <div
      class="modal modal--xl"
      role="dialog"
      aria-modal="true"
      aria-labelledby="storico-det-title"
      (click)="$event.stopPropagation()"
    >

      <!-- ─── Header ────────────────────────────────────────────── -->
      <header class="modal__header">
        <h2 id="storico-det-title" class="modal__title">
          <span aria-hidden="true">📚</span>
          Dettagli ordine

          @if (order?.or_order_number) {
            <span class="modal__title-tag">n° {{ order.or_order_number }}</span>
          }

          @if (order && isPartial()) {
            <span class="status-chip status-chip--partial"
              title="Ordine con consegna parziale">
              Parziale
            </span>
          }
        </h2>

        <button
          type="button"
          class="modal-close"
          (click)="close()"
          aria-label="Chiudi"
        >×</button>
      </header>

      <!-- ─── Body ──────────────────────────────────────────────── -->
      <div class="modal__body">

        @if (loading) {
          <div class="loading-card" aria-live="polite">
            <div class="loading-card__spinner" aria-hidden="true"></div>
            <p>Caricamento dettagli ordine…</p>
          </div>
        } @else if (order) {

          <!-- ─── Info top — 2 columns ─────────────────────────── -->
          <div class="info-cols">

            <!-- Col 1 — Ordine + cliente -->
            <section class="info-card">
              <header class="info-card__header">
                <h3 class="info-card__title">📋 Ordine &amp; cliente</h3>
              </header>

              <div class="info-grid">
                <div class="info-field">
                  <span class="info-field__label">N° Ordine</span>
                  <span class="info-field__value info-field__value--strong">
                    {{ order.or_order_number || '—' }}
                  </span>
                </div>

                <div class="info-field">
                  <span class="info-field__label">Data ordine</span>
                  <span class="info-field__value">{{ formattedDate }}</span>
                </div>

                <div class="info-field">
                  <span class="info-field__label">Data evasione</span>
                  <span class="info-field__value">{{ formattedClosing }}</span>
                </div>

                <div class="info-field info-field--full">
                  <span class="info-field__label">Cliente</span>
                  <span class="info-field__value">{{ order.or_customer || '—' }}</span>
                </div>

                <div class="info-field info-field--full">
                  <span class="info-field__label">Indirizzo di spedizione</span>
                  <span class="info-field__value">
                    {{ order.or_shipping_address || '—' }}
                  </span>
                </div>

                <div class="info-field">
                  <span class="info-field__label">Città</span>
                  <span class="info-field__value">
                    {{ order.or_shipping_city || '—' }}
                  </span>
                </div>

                <div class="info-field">
                  <span class="info-field__label">CAP</span>
                  <span class="info-field__value">
                    {{ order.or_shipping_zip || '—' }}
                  </span>
                </div>

                <div class="info-field">
                  <span class="info-field__label">Provincia</span>
                  <span class="info-field__value">
                    {{ order.or_shipping_prov || '—' }}
                  </span>
                </div>

                <div class="info-field info-field--full">
                  <span class="info-field__label">Nazione</span>
                  <span class="info-field__value">
                    {{ order.or_shipping_country || '—' }}
                  </span>
                </div>
              </div>
            </section>

            <!-- Col 2 — Operatore + corriere + counters -->
            <section class="info-card">
              <header class="info-card__header">
                <h3 class="info-card__title">⚙️ Esecuzione</h3>
              </header>

              <div class="info-grid info-grid--two">
                <div class="info-field">
                  <span class="info-field__label">Operatore</span>
                  <span class="info-field__value">
                    @if (order.vs_details?.ac_first_name || order.vs_details?.ac_last_name) {
                      {{ order.vs_details.ac_first_name }} {{ order.vs_details.ac_last_name }}
                    } @else { — }
                  </span>
                </div>

                <div class="info-field">
                  <span class="info-field__label">Corriere</span>
                  <span class="info-field__value">
                    {{ order.vs_details?.carrier_desc || '—' }}
                  </span>
                </div>

                <div class="info-field">
                  <span class="info-field__label">Priorità</span>
                  <span class="info-field__value">
                    {{ order.vs_details?.tab_order_priority_desc || '—' }}
                  </span>
                </div>

                <div class="info-field">
                  <span class="info-field__label">Tempo preparazione</span>
                  <span class="info-field__value info-field__value--strong">
                    {{ preparationTime }}
                  </span>
                </div>

                <div class="info-field">
                  <span class="info-field__label">N° Articoli</span>
                  <span class="info-field__value info-field__value--strong">
                    {{ order.products?.length ?? 0 }}
                  </span>
                </div>

                <div class="info-field">
                  <span class="info-field__label">N° Colli</span>
                  <span class="info-field__value info-field__value--strong">
                    {{ order.boxes?.length ?? 0 }}
                  </span>
                </div>
              </div>
            </section>
          </div>

          <!-- ─── Accordions ─────────────────────────────────────── -->
          <div class="accordion">

            <!--
              📡 Tracking — v2.12.0 L.2: reuse del widget tracking già
              usato in status-spedizioni e nei list pages agente/cliente
              (post-v2.4.0 è content-only, parent owns chrome).
              shipment_id = order.order_id viene passato come param BE
              `/get_shipments_status/{orderId}` (vedi v2.11.1 hotfix #1
              per il naming ambiguo del widget @Input).
            -->
            <section class="accordion__item">
              <button
                type="button"
                class="accordion__header"
                [class.accordion__header--open]="openSection === 'tracking'"
                (click)="toggleSection('tracking')"
                [attr.aria-expanded]="openSection === 'tracking'"
              >
                <span class="accordion__icon" aria-hidden="true">📡</span>
                <span class="accordion__title">Tracking spedizione</span>
                <span class="accordion__chevron" aria-hidden="true">
                  @if (openSection === 'tracking') { ▾ } @else { ▸ }
                </span>
              </button>

              @if (openSection === 'tracking') {
                <div class="accordion__body accordion__body--flush">
                  <app-accesso-funzionari-tracking
                    [shipment_id]="order.order_id"
                  ></app-accesso-funzionari-tracking>
                </div>
              }
            </section>

            <!-- Prodotti (embed app-product-details-storico H.5) -->
            <section class="accordion__item">
              <button
                type="button"
                class="accordion__header"
                [class.accordion__header--open]="openSection === 'products'"
                (click)="toggleSection('products')"
                [attr.aria-expanded]="openSection === 'products'"
              >
                <span class="accordion__icon" aria-hidden="true">📦</span>
                <span class="accordion__title">Prodotti</span>
                <span class="accordion__count">{{ order.products?.length ?? 0 }}</span>
                <span class="accordion__chevron" aria-hidden="true">
                  @if (openSection === 'products') { ▾ } @else { ▸ }
                </span>
              </button>

              @if (openSection === 'products') {
                <div class="accordion__body accordion__body--flush">
                  <app-product-details-storico
                    [order]="order"
                    [opened]="true"
                  ></app-product-details-storico>
                </div>
              }
            </section>

            <!-- Colli grouped -->
            <section class="accordion__item">
              <button
                type="button"
                class="accordion__header"
                [class.accordion__header--open]="openSection === 'boxes'"
                (click)="toggleSection('boxes')"
                [attr.aria-expanded]="openSection === 'boxes'"
              >
                <span class="accordion__icon" aria-hidden="true">🗳️</span>
                <span class="accordion__title">Colli</span>
                <span class="accordion__count">{{ order.boxes?.length ?? 0 }}</span>
                <span class="accordion__chevron" aria-hidden="true">
                  @if (openSection === 'boxes') { ▾ } @else { ▸ }
                </span>
              </button>

              @if (openSection === 'boxes') {
                <div class="accordion__body">
                  @if (groupedBoxes.length > 0) {
                    <ul class="line-list" role="list">
                      @for (g of groupedBoxes; track g.ts_box_type_desc) {
                        <li class="line-row">
                          <span class="line-row__desc">
                            {{ g.ts_box_type_desc || '—' }}
                          </span>
                          <span class="line-row__qty">×{{ g.quantity }}</span>
                        </li>
                      }
                    </ul>
                  } @else {
                    <p class="accordion__empty">Nessun collo registrato.</p>
                  }
                </div>
              }
            </section>

            <!-- Documenti + Etichette unificati -->
            <section class="accordion__item">
              <button
                type="button"
                class="accordion__header"
                [class.accordion__header--open]="openSection === 'docs'"
                (click)="toggleSection('docs')"
                [attr.aria-expanded]="openSection === 'docs'"
              >
                <span class="accordion__icon" aria-hidden="true">📎</span>
                <span class="accordion__title">Documenti &amp; Etichette</span>
                <span class="accordion__count">
                  {{
                    (documents?.length ?? 0) +
                    (linkAllLabels ? 1 : 0) +
                    (isLinkToDDT ? 1 : 0) +
                    (linkCarrier ? 1 : 0) +
                    (linkBordero ? 1 : 0)
                  }}
                </span>
                <span class="accordion__chevron" aria-hidden="true">
                  @if (openSection === 'docs') { ▾ } @else { ▸ }
                </span>
              </button>

              @if (openSection === 'docs') {
                <div class="accordion__body">

                  <!-- Documenti generici -->
                  @if (documents && documents.length > 0) {
                    <h4 class="accordion__subtitle">Documenti</h4>
                    <ul class="line-list" role="list">
                      @for (doc of documents; track doc.document_id) {
                        <li class="line-row">
                          <span class="line-row__desc">{{ doc.td_document }}</span>
                          @if (doc.td_file) {
                            <a
                              class="file-link"
                              [href]="doc.td_file"
                              target="_blank"
                              rel="noopener"
                            >📄 Apri</a>
                          }
                        </li>
                      }
                    </ul>
                  }

                  <!-- Etichette PDF -->
                  <h4 class="accordion__subtitle">Etichette</h4>
                  @if (linkAllLabels || isLinkToDDT || linkCarrier || linkBordero) {
                    <div class="label-grid">
                      @if (linkAllLabels) {
                        <a
                          class="label-card"
                          [href]="linkAllLabels"
                          target="_blank"
                          rel="noopener"
                          title="Apri etichette Scatole (PDF)"
                        >
                          <span class="label-card__icon" aria-hidden="true">📦</span>
                          <span class="label-card__title">Scatole</span>
                          <span class="label-card__sub">PDF</span>
                        </a>
                      }

                      @if (isLinkToDDT && linkToDDT) {
                        <a
                          class="label-card"
                          [href]="linkToDDT"
                          target="_blank"
                          rel="noopener"
                          title="Apri DDT (PDF)"
                        >
                          <span class="label-card__icon" aria-hidden="true">🧾</span>
                          <span class="label-card__title">DDT</span>
                          <span class="label-card__sub">PDF</span>
                        </a>
                      }

                      @if (linkCarrier) {
                        <a
                          class="label-card"
                          [href]="linkCarrier"
                          target="_blank"
                          rel="noopener"
                          title="Apri etichetta corriere (PDF)"
                        >
                          <span class="label-card__icon" aria-hidden="true">🚚</span>
                          <span class="label-card__title">Corriere</span>
                          <span class="label-card__sub">PDF</span>
                        </a>
                      }

                      @if (linkBordero) {
                        <a
                          class="label-card"
                          [href]="linkBordero"
                          target="_blank"
                          rel="noopener"
                          title="Apri borderò (PDF)"
                        >
                          <span class="label-card__icon" aria-hidden="true">📋</span>
                          <span class="label-card__title">Borderò</span>
                          <span class="label-card__sub">PDF</span>
                        </a>
                      }
                    </div>
                  } @else {
                    <p class="accordion__empty">Nessuna etichetta disponibile.</p>
                  }
                </div>
              }
            </section>

            <!-- Errori (sempre visibile, drop legacy feature gate) -->
             <!-- Not improtant for now, implemented but not to be used atm
            <section class="accordion__item">
              <button
                type="button"
                class="accordion__header"
                [class.accordion__header--open]="openSection === 'errors'"
                (click)="toggleSection('errors')"
                [attr.aria-expanded]="openSection === 'errors'"
              >
                <span class="accordion__icon" aria-hidden="true">⚠️</span>
                <span class="accordion__title">Errori</span>
                <span
                  class="accordion__count"
                  [class.accordion__count--danger]="errorsArray.length > 0"
                >{{ errorsArray.length }}</span>
                <span class="accordion__chevron" aria-hidden="true">
                  @if (openSection === 'errors') { ▾ } @else { ▸ }
                </span>
              </button>

              @if (openSection === 'errors') {
                <div class="accordion__body">

                  @if (errorsArray.length === 0) {
                    <p class="accordion__empty">Nessun errore registrato per questo ordine.</p>
                  } @else {
                    <div class="errors-table-scroll">
                      <table class="errors-table" role="table">
                        <thead>
                          <tr>
                            <th class="col-id">ID</th>
                            <th class="col-type">Tipo</th>
                            <th class="col-desc">Descrizione</th>
                            <th class="col-gravity">Gravità</th>
                            <th class="col-actions">Azioni</th>
                          </tr>
                        </thead>
                        <tbody>
                          @for (err of errorsArray; track err.order_error_id) {
                            <tr class="error-row">
                              <td class="col-id">
                                <span class="cell-id">{{ err.order_error_id }}</span>
                              </td>
                              <td class="col-type">
                                <span class="cell-type">
                                  {{ err.order_error_type_description || '—' }}
                                </span>
                              </td>
                              <td class="col-desc">
                                <span class="cell-desc">
                                  {{ err.oe_description || '—' }}
                                </span>
                              </td>
                              <td class="col-gravity">
                                <span
                                  class="cell-gravity"
                                  [class.cell-gravity--high]="(err.oe_error_gravity ?? 0) >= 7"
                                  [class.cell-gravity--mid]="(err.oe_error_gravity ?? 0) >= 4 && (err.oe_error_gravity ?? 0) < 7"
                                >{{ err.oe_error_gravity ?? '—' }}</span>
                              </td>
                              <td class="col-actions">
                                <div class="row-actions">
                                  <button
                                    type="button"
                                    class="icon-btn icon-btn--primary"
                                    (click)="enableModifyError(err)"
                                    title="Modifica errore"
                                    aria-label="Modifica errore"
                                  >✏️</button>
                                  <button
                                    type="button"
                                    class="icon-btn icon-btn--danger"
                                    (click)="deleteError(err.order_error_id)"
                                    title="Elimina errore"
                                    aria-label="Elimina errore"
                                  >🗑️</button>
                                </div>
                              </td>
                            </tr>
                          }
                        </tbody>
                      </table>
                    </div>
                  }

                  <div class="accordion__cta-row">
                    <button
                      type="button"
                      class="btn btn--add-error"
                      (click)="addError()"
                    >
                      <span aria-hidden="true">➕</span>
                      Aggiungi errore
                    </button>
                  </div>
                </div>
              }
            </section>
            -->
          </div>
        }
      </div>

      <!-- ─── Footer ────────────────────────────────────────────── -->
      @if (!loading && order) {
        <footer class="modal__footer">
          <button type="button" class="btn btn--ghost" (click)="close()">
            Chiudi
          </button>
        </footer>
      }
    </div>
  </div>
}

<!--
  Sub-modali nested (z-index 2100, sopra a 2000):

  Nota: il sub-modal `app-product-detail-modal` è gestito da H.5
  (embedded `<app-product-details-storico>` ha già il proprio block
  per app-product-detail-modal). Il legacy H.2 aveva un block
  duplicato — rimosso per evitare doppio rendering del modale
  quando l'utente clicca ℹ️ su un prodotto dall'accordion Prodotti.
-->
  @if (closeServ.isAgentOrderTrack) {
  <div class="tracking-backdrop" (click)="closeTracking()">
    <div
      class="tracking-modal"
      (click)="$event.stopPropagation()"
      role="dialog"
      aria-modal="true"
      aria-labelledby="tracking-title"
    >
      <header class="tracking-modal__header">
        <h2 id="tracking-title" class="tracking-modal__title">
          <span aria-hidden="true">📡</span>
          Tracking spedizione
        </h2>
        <button
          type="button"
          class="tracking-modal__close"
          (click)="closeTracking()"
          aria-label="Chiudi"
        >×</button>
      </header>
      <div class="tracking-modal__body">
        <app-accesso-funzionari-tracking
          [shipment_id]="shipment_id"
          [openTrack]="closeServ.isAgentOrderTrack"
        ></app-accesso-funzionari-tracking>
      </div>
    </div>
  </div>
}

@if (closeServ.openError | async) {
  <app-errori
    [order_id]="+order_id"
    (modalClosed1)="reloadErrors()"
  ></app-errori>
}

@if (closeServ.modifyError | async) {
  <app-modify-error
    [error]="errorToModify"
    [order_id]="+order_id"
    (modalClosed1)="reloadErrors()"
  ></app-modify-error>
}
